import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";

import { css, html, LitElement } from "lit";
import { customElement, state, query, property } from "lit/decorators.js";
import { Style } from "../Styles";
import { choose } from "lit/directives/choose.js";
import { confirmResetPassword } from "aws-amplify/auth";
import { resetPassword } from "aws-amplify/auth";
import { validatePassword, validateVerificationCode } from "./Utils";
import { validateEmail } from "../../Utils";

@customElement("ds-auth-forgot-password-request")
export class AuthForgotPasswordRequest extends LitElement {
    static override styles = [
        Style,
        css`
            .button-row {
                margin-top: 1em;
            }
        `,
    ];

    @property({ type: String }) email: string | undefined;

    @state() protected _valid = false;
    @state() protected _errorMsg: string | undefined = undefined;
    @query("#email") protected _emailControl: HTMLInputElement | undefined;

    private async _submit() {
        this._errorMsg = undefined;
        try {
            if (this._emailControl) {
                await resetPassword({ username: this._emailControl.value });
            }
        } catch (ex: any) {
            // we deliberately show no errors so people cannot figure out who has an
            // account or does not
        }
        this.dispatchEvent(
            new CustomEvent("on-submit", {
                bubbles: true,
                composed: true,
                detail: {},
            })
        );
    }

    private _validateForm() {
        this._valid = validateEmail(this._emailControl?.value);
        return this._valid;
    }

    private _reset() {}
    override render() {
        return html`<div>
            <form>
                <p>
                    Please enter your email address so we can send you a code to reset your
                    password.
                </p>
                <sl-input
                    autofocus
                    class="custom-input"
                    id="email"
                    label="Email address"
                    clearable
                    required
                    size="medium"
                    placeholder="bob@bob.net"
                    @sl-input="${this._validateForm}"
                    required
                    filled
                    .value=${this.email ?? ""}
                >
                </sl-input>
                <div>
                    <h4
                        class="surface-error-color"
                        ?hidden="${!this._errorMsg || this._errorMsg.length === 0}"
                    >
                        ${this._errorMsg}
                    </h4>
                </div>
                <div class="button-row">
                    <sl-button
                        variant="default"
                        @click=${() => {
                            this.dispatchEvent(
                                new CustomEvent("on-have-code", {
                                    bubbles: true,
                                    composed: true,
                                })
                            );
                        }}
                        >I have a code</sl-button
                    >
                    <sl-button
                        style="margin-left:auto"
                        variant="default"
                        @click=${() => {
                            this._reset();
                            this.dispatchEvent(
                                new CustomEvent("on-cancel", {
                                    bubbles: true,
                                    composed: true,
                                })
                            );
                        }}
                        >Cancel</sl-button
                    >

                    <sl-button
                        variant="success"
                        @click=${(e: Event) => {
                            e.stopPropagation();
                            this._submit();
                        }}
                        ?disabled="${!this._valid}"
                        >Submit</sl-button
                    >
                </div>
            </form>
        </div>`;
    }
}

@customElement("ds-auth-forgot-password-submit")
export class AuthForgotPasswordSubmit extends LitElement {
    static override styles = [
        Style,
        css`
            .button-row {
                margin-top: 1em;
            }
        `,
    ];

    @property({ type: String }) email: string | undefined;

    @state() protected _valid = false;
    @state() protected _errorMsg: string | undefined;

    @query("#email") protected _emailControl: HTMLInputElement | undefined;
    @query("#verification-code") protected _verificationControl: HTMLInputElement | undefined;
    @query("#new-password") protected _passwordControl: HTMLInputElement | undefined;

    private async _submit() {
        this._errorMsg = undefined;
        try {
            if (this._emailControl && this._verificationControl && this._passwordControl) {
                await confirmResetPassword({
                    username: this._emailControl.value,
                    confirmationCode: this._verificationControl.value,
                    newPassword: this._passwordControl.value,
                });
                this.dispatchEvent(
                    new CustomEvent("on-success", {
                        bubbles: true,
                        composed: true,
                    })
                );
            }
        } catch (error: any) {
            this._errorMsg = error.toString();
        }
    }

    private _validateForm() {
        this._valid =
            validateEmail(this._emailControl?.value) &&
            validateVerificationCode(this._verificationControl?.value) &&
            validatePassword(this._passwordControl?.value);
        return false;
    }

    private _reset() {}

    override render() {
        return html`<div>
            <p>Check your email for a confirmation code</p>
            <form>
                <sl-input
                    autofocus
                    class="custom-input"
                    id="email"
                    label="Email address"
                    clearable
                    required
                    size="medium"
                    placeholder="bob@bob.net"
                    @sl-input="${this._validateForm}"
                    required
                    filled
                    .value=${this.email ?? ""}
                >
                </sl-input>
                <sl-input
                    class="custom-input"
                    id="verification-code"
                    label="Verification code"
                    clearable
                    required
                    size="medium"
                    placeholder="xxxxxx"
                    @sl-input="${this._validateForm}"
                    required
                    filled
                ></sl-input>
                <sl-input
                    class="custom-input"
                    id="new-password"
                    label="New password"
                    type="password"
                    passwordToggle
                    clearable
                    required
                    size="medium"
                    placeholder="xxxxxx"
                    @sl-input="${this._validateForm}"
                    required
                    filled
                ></sl-input>
                <div>
                    <h4
                        class="surface-error-color"
                        ?hidden="${!this._errorMsg || this._errorMsg.length === 0}"
                    >
                        ${this._errorMsg}
                    </h4>
                </div>
                <div class="button-row">
                    <sl-button
                        variant="default"
                        @click=${() => {
                            this.dispatchEvent(
                                new CustomEvent("on-request-code", {
                                    bubbles: true,
                                    composed: true,
                                })
                            );
                        }}
                        >Request code</sl-button
                    >
                    <sl-button
                        style="margin-left:auto"
                        variant="default"
                        @click=${() => {
                            this._reset();
                            this.dispatchEvent(
                                new CustomEvent("on-cancel", {
                                    bubbles: true,
                                    composed: true,
                                })
                            );
                        }}
                        >Cancel</sl-button
                    >

                    <sl-button
                        variant="success"
                        @click=${(e: Event) => {
                            e.stopPropagation();
                            this._submit();
                        }}
                        ?disabled="${!this._valid}"
                        >Submit</sl-button
                    >
                    <br />
                </div>
            </form>
        </div>`;
    }
}

@customElement("ds-auth-forgot-password-success")
export class AuthForgotPasswordSuccess extends LitElement {
    static override styles = [Style, css``];

    override render() {
        return html`<div>
            <div>
                <p>Your password has been changed, please sign in.</p>
            </div>
            <div class="button-row">
                <sl-button
                    style="margin-left:auto"
                    variant="default"
                    @click=${() => {
                        location.reload();
                    }}
                    >OK</sl-button
                >

                <br />
            </div>
        </div>`;
    }
}

type ForgotPasswordModeT = "request" | "submit" | "success";

@customElement("ds-auth-forgot-password")
export class AuthForgotPassword extends LitElement {
    static override styles = [Style, css``];

    @property({ type: String }) email: string | undefined;
    @property({ type: String }) mode: ForgotPasswordModeT = "request";

    reset(): void {
        this.mode = "request";
    }

    override render() {
        return html`${choose(this.mode, [
            [
                "request",
                () =>
                    html`<ds-auth-forgot-password-request
                        .email=${this.email ?? ""}
                        @on-submit="${() => (this.mode = "submit")}"
                        @on-have-code="${() => (this.mode = "submit")}"
                    ></ds-auth-forgot-password-request>`,
            ],
            [
                "submit",
                () => html`<ds-auth-forgot-password-submit
                    .email=${this.email ?? ""}
                    @on-request-code="${() => (this.mode = "request")}"
                    @on-success="${(e: Event) => {
                        this.mode = "success";
                        e.stopPropagation();
                    }}"
                ></ds-auth-forgot-password-submit>`,
            ],
            [
                "success",
                () =>
                    html`<ds-auth-forgot-password-success
                        @on-cancel=${() => (this.mode = "request")}
                    ></ds-auth-forgot-password-success>`,
            ],
        ])}`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-auth-forgot-password-request": AuthForgotPasswordRequest;
        "ds-auth-forgot-password-submit": AuthForgotPasswordSubmit;
        "ds-auth-forgot-password-success": AuthForgotPasswordSuccess;
        "ds-auth-forgot-password": AuthForgotPassword;
    }
}
